// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import { joyoKanji } from "../../consts/joyoKanji";
import { jinmeiyoKanji } from "../../consts/jinmeiyoKanji";
import { wanikaniSrsStages, getSrsStageInfo } from "../../consts/wanikaniSrsStages";
import { useWanikaniProgress } from "../../hooks/useWanikaniProgress";
import WanikaniQuizComponent, { WanikaniQuizSummary } from "./WanikaniQuizComponent";
import { buildQuizQueue } from "./wanikaniQuizHelpers";
import Seo from "../Seo/SeoComponent";
import * as analytics from "../../utils/analytics";
import styles from "./wanikaniPage.module.scss";

function ConnectForm({ onConnect, error, isSyncing }) {
    const { t } = useTranslation("wanikaniPage");
    const [token, setToken] = useState("");

    return (
        <div className={styles.card}>
            <h2 className={styles.cardTitle}>{t("connectForm.title")}</h2>
            <p className={styles.cardText}>{t("connectForm.description")}</p>
            <ol className={styles.stepsList}>
                <li>
                    {t("connectForm.step1Prefix")}{" "}
                    <a href="https://www.wanikani.com/settings/personal_access_tokens" target="_blank" rel="noreferrer">
                        wanikani.com/settings/personal_access_tokens
                    </a>
                </li>
                <li>{t("connectForm.step2")}</li>
                <li>{t("connectForm.step3")}</li>
            </ol>
            <form
                className={styles.connectForm}
                onSubmit={(event) => {
                    event.preventDefault();
                    onConnect(token);
                }}
            >
                <input
                    type="password"
                    className={styles.tokenInput}
                    placeholder={t("connectForm.tokenPlaceholder")}
                    value={token}
                    onChange={(event) => setToken(event.target.value)}
                    aria-label={t("connectForm.tokenPlaceholder")}
                />
                <button type="submit" className={styles.quizPrimaryButton} disabled={!token.trim() || isSyncing}>
                    {isSyncing ? t("connectForm.connecting") : t("connectForm.connect")}
                </button>
            </form>
            {error && (
                <p className={styles.errorText} role="alert">
                    {error}
                </p>
            )}
            <p className={styles.privacyNote}>{t("connectForm.privacyNote")}</p>
        </div>
    );
}

function SrsBreakdown({ progressByKanji }) {
    const counts = useMemo(() => {
        const byGroup = new Map();
        Object.values(progressByKanji).forEach((progress) => {
            const info = getSrsStageInfo(progress.srsStage);
            byGroup.set(info.group, (byGroup.get(info.group) || 0) + 1);
        });
        return byGroup;
    }, [progressByKanji]);

    const groups = [...new Set(wanikaniSrsStages.map((s) => s.group))];
    const max = Math.max(...groups.map((group) => counts.get(group) || 0), 1);

    return (
        <ul className={styles.barList}>
            {groups.map((group) => {
                const info = wanikaniSrsStages.find((s) => s.group === group);
                const count = counts.get(group) || 0;
                return (
                    <li key={group} className={styles.barRow}>
                        <span className={styles.barLabel}>{info.label.replace(/ (I|II|III|IV)$/, "")}</span>
                        <span className={styles.barTrack}>
                            <span
                                className={styles.barFill}
                                style={{ width: `${Math.max((count / max) * 100, count ? 4 : 0)}%`, background: info.color }}
                            />
                        </span>
                        <span className={styles.barValue}>{count}</span>
                    </li>
                );
            })}
        </ul>
    );
}

const wanikaniWallKanji = [...joyoKanji, ...jinmeiyoKanji];

function KanjiWall({ progressByKanji }) {
    const { t } = useTranslation("wanikaniPage");
    const backToKanjiState = { from: "/wanikani", label: t("backLinkLabel") };
    return (
        <div className={styles.kanjiWall}>
            {wanikaniWallKanji.map((symbol) => {
                const progress = progressByKanji[symbol];
                const info = getSrsStageInfo(progress?.srsStage ?? 0);
                const isKnown = Boolean(progress);
                return (
                    <Link
                        key={symbol}
                        to={`/kanji/${symbol}`}
                        state={backToKanjiState}
                        className={styles.kanjiWallTile}
                        title={isKnown ? info.label : t("kanjiWall.notYetTracked")}
                        style={
                            isKnown
                                ? { color: info.color, background: `${info.color}1a` }
                                : undefined
                        }
                    >
                        {symbol}
                    </Link>
                );
            })}
        </div>
    );
}

function Dashboard({ progress, onStartPractice, practiceNotice }) {
    const { t } = useTranslation("wanikaniPage");
    const { level, lessonsAvailable, reviewsAvailable, progressByKanji, error, isSyncing, refresh, disconnect } = progress;
    const knownCount = Object.keys(progressByKanji).length;

    return (
        <>
            {error && (
                <p className={styles.errorText} role="alert">
                    {error}
                </p>
            )}

            <div className={styles.heroRow}>
                <div className={styles.heroTile}>
                    <span className={styles.heroLabel}>{t("dashboard.level")}</span>
                    <span className={styles.heroValue}>{level}</span>
                </div>
                <div className={styles.heroTile}>
                    <span className={styles.heroLabel}>{t("dashboard.reviewsAvailable")}</span>
                    <span className={styles.heroValue}>{reviewsAvailable ?? "—"}</span>
                </div>
                <div className={styles.heroTile}>
                    <span className={styles.heroLabel}>{t("dashboard.lessonsAvailable")}</span>
                    <span className={styles.heroValue}>{lessonsAvailable ?? "—"}</span>
                </div>
            </div>

            <div className={styles.card}>
                <h2 className={styles.cardTitle}>{t("dashboard.srsStageTitle", { count: knownCount })}</h2>
                <SrsBreakdown progressByKanji={progressByKanji} />
            </div>

            <div className={styles.card}>
                <h2 className={styles.cardTitle}>{t("kanjiWall.title")}</h2>
                <p className={styles.cardText}>{t("kanjiWall.description")}</p>
                <KanjiWall progressByKanji={progressByKanji} />
            </div>

            {practiceNotice && (
                <p className={styles.errorText} role="alert">
                    {practiceNotice}
                </p>
            )}

            <div className={styles.actionsRow}>
                <button type="button" className={styles.quizPrimaryButton} onClick={onStartPractice}>
                    {t("dashboard.practiceDueKanji")}
                </button>
                <button type="button" className={styles.quizSecondaryButton} onClick={refresh} disabled={isSyncing}>
                    {isSyncing ? t("dashboard.syncing") : t("dashboard.refresh")}
                </button>
                <button type="button" className={styles.quizSecondaryButton} onClick={disconnect}>
                    {t("dashboard.disconnect")}
                </button>
            </div>
        </>
    );
}

function WanikaniPage() {
    const { t, i18n } = useTranslation("wanikaniPage");
    const progress = useWanikaniProgress();
    const [mode, setMode] = useState("dashboard"); // dashboard | quiz | summary
    const [queue, setQueue] = useState([]);
    const [result, setResult] = useState(null);
    const [practiceNotice, setPracticeNotice] = useState(null);
    const quizStartedAtRef = useRef(null);

    const isPt = i18n.language === "pt";
    const currentPath = isPt ? "/pt/wanikani" : "/wanikani";

    function startPractice() {
        const nextQueue = buildQuizQueue(progress.progressByKanji);
        if (!nextQueue.length) {
            setPracticeNotice(t("dashboard.noKanjiDue"));
            return;
        }
        setPracticeNotice(null);
        setQueue(nextQueue);
        setMode("quiz");
        quizStartedAtRef.current = Date.now();
        analytics.track("quiz_started", { quiz_type: "wanikani", question_count: nextQueue.length });
    }

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
            />
            <Header />
            <main className={styles.main}>
                <section className={styles.container}>
                    <h1 className={styles.title}>{t("title")}</h1>
                    <p className={styles.description}>{t("description")}</p>

                    {progress.status === "disconnected" || progress.status === "error" ? (
                        <ConnectForm onConnect={progress.connect} error={progress.error} isSyncing={progress.isSyncing} />
                    ) : progress.status === "loading" ? (
                        <p className={styles.cardText}>{t("loading")}</p>
                    ) : mode === "dashboard" ? (
                        <Dashboard progress={progress} onStartPractice={startPractice} practiceNotice={practiceNotice} />
                    ) : mode === "quiz" ? (
                        <WanikaniQuizComponent
                            queue={queue}
                            onExit={() => setMode("dashboard")}
                            onFinish={(finalResult) => {
                                setResult(finalResult);
                                setMode("summary");
                                const durationSeconds = quizStartedAtRef.current
                                    ? Math.round((Date.now() - quizStartedAtRef.current) / 1000)
                                    : null;
                                analytics.track("quiz_completed", {
                                    quiz_type: "wanikani",
                                    question_count: queue.length,
                                    correct_count: finalResult.score?.correct ?? null,
                                    wrong_count: finalResult.score?.wrong ?? null,
                                    duration_seconds: durationSeconds,
                                });
                            }}
                        />
                    ) : (
                        <WanikaniQuizSummary
                            score={result.score}
                            missed={result.missed}
                            onExit={() => setMode("dashboard")}
                            onRestart={startPractice}
                        />
                    )}

                    {progress.status === "ready" && Object.keys(progress.progressByKanji).length === 0 && (
                        <p className={styles.cardText}>{t("noKanjiYet")}</p>
                    )}
                </section>
            </main>
            <Footer />
        </div>
    );
}

export default WanikaniPage;
